import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useApi, useMutation } from '../lib/use-api';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { GroupDialog, type GroupFormValue } from '../components/GroupDialog';
import { AddMemberDialog } from '../components/AddMemberDialog';

interface GroupSummary {
  principalId: string;
  displayName: string;
  description: string | null;
  joinCode: string;
  autoApprove: boolean;
  joinEnabled: boolean;
  memberCount: number;
  pendingCount: number;
}

interface GroupMember {
  principalId: string;
  displayName: string;
  status: string;
  roleInGroup: string;
}

/**
 * Nhóm học viên — plan §6.6.
 *
 * Mã tham gia hiện ngay trên bảng: giáo viên cần đọc nó cho cả lớp, giấu sau
 * một lần bấm chỉ làm chậm việc đó mà không thêm an toàn nào (ai xem được
 * trang này thì cũng bấm được).
 *
 * ĐÂY KHÔNG PHẢI LỚP HỌC (CLAUDE.md §11): không sổ điểm, không điểm danh.
 */
export function GroupsPage(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<GroupFormValue | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [addingTo, setAddingTo] = useState<GroupSummary | undefined>(undefined);

  const groups = useApi<{ items: GroupSummary[] }>('/api/groups');

  const members = useApi<{ items: GroupMember[] }>(
    `/api/groups/${expanded}/members`,
    {},
    expanded !== undefined,
  );

  const approve = useMutation<{ groupId: string; memberId: string }, void>(
    useCallback(
      (input: { groupId: string; memberId: string }) =>
        apiFetch<void>(`/api/groups/${input.groupId}/members/${input.memberId}/approve`, {
          method: 'POST',
        }),
      [],
    ),
  );

  const remove = useMutation<{ groupId: string; memberId: string }, void>(
    useCallback(
      (input: { groupId: string; memberId: string }) =>
        apiFetch<void>(`/api/groups/${input.groupId}/members/${input.memberId}`, {
          method: 'DELETE',
        }),
      [],
    ),
  );

  const regenerate = useMutation<string, { joinCode: string }>(
    useCallback(
      (groupId: string) =>
        apiFetch<{ joinCode: string }>(`/api/groups/${groupId}/join-code`, { method: 'POST' }),
      [],
    ),
  );

  const reloadMembers = (): void => {
    members.reload();
    groups.reload();
  };

  // Gỡ người khỏi lớp là thao tác người khác nhìn thấy hậu quả (mất bài đang
  // giao), nên hỏi lại. Sinh lại mã cũng vậy: mã cũ chết ngay lập tức.
  const confirmRemove = async (groupId: string, member: GroupMember): Promise<void> => {
    if (!window.confirm(t('groups.confirmRemove', { name: member.displayName }))) return;
    await remove.run({ groupId, memberId: member.principalId });
    reloadMembers();
  };

  const confirmRegenerate = async (group: GroupSummary): Promise<void> => {
    if (!window.confirm(t('groups.confirmRegenerate'))) return;
    await regenerate.run(group.principalId);
    groups.reload();
  };

  const memberIds = useMemo(
    () => new Set((members.data?.items ?? []).map((member) => member.principalId)),
    [members.data],
  );

  return (
    <section>
      <h1>{t('groups.heading')}</h1>

      <div className="toolbar">
        <button type="button" onClick={() => setCreating(true)}>
          {t('groups.create')}
        </button>
      </div>

      {regenerate.error ? <ErrorNotice error={regenerate.error} /> : null}
      {groups.loading ? <Loading /> : null}
      {groups.error ? <ErrorNotice error={groups.error} onRetry={groups.reload} /> : null}

      {groups.data && !groups.loading ? (
        groups.data.items.length === 0 ? (
          <EmptyNotice message={t('groups.empty')} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('groups.column.name')}</th>
                <th scope="col">{t('groups.column.joinCode')}</th>
                <th scope="col">{t('groups.column.members')}</th>
                <th scope="col">{t('library.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.data.items.map((group) => (
                <tr key={group.principalId}>
                  <td>
                    {group.displayName}
                    {!group.joinEnabled ? (
                      <span className="chip chip--static">{t('groups.joinClosed')}</span>
                    ) : null}
                  </td>
                  <td className="mono">{group.joinCode}</td>
                  <td>
                    {group.memberCount}
                    {group.pendingCount > 0
                      ? ` (${t('groups.pendingCount', { count: group.pendingCount })})`
                      : ''}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded(expanded === group.principalId ? undefined : group.principalId)
                      }
                    >
                      {t('groups.viewMembers')}
                    </button>
                    <button type="button" onClick={() => setAddingTo(group)}>
                      {t('groups.addMember')}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({
                          principalId: group.principalId,
                          displayName: group.displayName,
                          description: group.description,
                          autoApprove: group.autoApprove,
                          joinEnabled: group.joinEnabled,
                        })
                      }
                    >
                      {t('common:action.edit')}
                    </button>
                    <button
                      type="button"
                      disabled={regenerate.pending}
                      onClick={() => void confirmRegenerate(group)}
                    >
                      {t('groups.regenerateCode')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      {expanded && members.data ? (
        <>
          <h2>{t('groups.membersHeading')}</h2>
          {approve.error ? <ErrorNotice error={approve.error} /> : null}
          {remove.error ? <ErrorNotice error={remove.error} /> : null}
          {members.data.items.length === 0 ? (
            <EmptyNotice message={t('groups.noMembers')} />
          ) : (
            <ul className="group-list">
              {members.data.items.map((member) => (
                <li key={member.principalId}>
                  <strong>{member.displayName}</strong>
                  <span className="chip chip--static">
                    {t(`groups.memberStatus.${member.status}`, { defaultValue: member.status })}
                  </span>
                  {member.status === 'pending' ? (
                    <button
                      type="button"
                      onClick={() =>
                        void approve
                          .run({ groupId: expanded, memberId: member.principalId })
                          .then(reloadMembers)
                      }
                    >
                      {t('groups.approve')}
                    </button>
                  ) : null}
                  {member.status !== 'removed' ? (
                    <button
                      type="button"
                      disabled={remove.pending}
                      onClick={() => void confirmRemove(expanded, member)}
                    >
                      {t('groups.removeMember')}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {creating ? (
        <GroupDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            groups.reload();
          }}
        />
      ) : null}

      {editing ? (
        <GroupDialog
          group={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            groups.reload();
          }}
        />
      ) : null}

      {addingTo ? (
        <AddMemberDialog
          groupId={addingTo.principalId}
          groupName={addingTo.displayName}
          existingMemberIds={memberIds}
          onClose={() => setAddingTo(undefined)}
          onAdded={reloadMembers}
        />
      ) : null}
    </section>
  );
}
